import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { formatMoney, parseMoney } from '@eventespresso/utils';
import { InlineEditText } from '../InlineEditInput';

import type { InlineEditProps } from '../InlineEditInput';
import type { InlineEditCurrencyProps } from './types';

import './style.scss';

const nullFunc = () => null;

export const InlineEditCurrency: React.FC<InlineEditCurrencyProps> = ({
	amount = 0,
	afterAmount,
	beforeAmount,
	formatAmount = formatMoney(2), // default is supplied for storybook only
	parseAmount = parseMoney,
	pattern = /.*/,
	id = '',
	isEditDisabled,
	onChange = nullFunc,
	tag = 'div',
	wrapperProps,
	vertical,
	tooltip,
}) => {
	const [nowValue, setNowValue] = useState<string>(formatAmount(amount));
	const [beforeValue, setBeforeValue] = useState<string>(formatAmount(amount));

	const className = classNames('ee-currency-input', vertical && 'ee-currency-input--vertical');
	const before = beforeAmount ? <span className={'ee-currency-input__before-amount'}>{beforeAmount} </span> : '';
	const after = afterAmount ? <span className={'ee-currency-input__after-amount'}> {afterAmount}</span> : '';
	const Wrapper = tag;

	const onChangeHandler = useCallback<InlineEditProps['onChange']>(
		(submitValue) => {
			const newAmount = parseAmount ? parseAmount(submitValue) : submitValue;
			onChange({ amount: newAmount, id });
			setNowValue(formatAmount(newAmount));
		},
		[id, onChange, parseAmount, formatAmount]
	);

	// detect changes from the parent
	useEffect(() => {
		setNowValue(formatAmount(amount));
	}, [amount, formatAmount]);

	useEffect(() => {
		if (!pattern.test(nowValue)) {
			// illegal chars
			setNowValue(beforeValue);
		} else {
			// safe input
			setBeforeValue(nowValue);
		}
	}, [pattern, beforeValue, nowValue]);

	return (
		<Wrapper {...wrapperProps} className={className}>
			{before}
			<InlineEditText
				as='span'
				isDisabled={isEditDisabled}
				key={id}
				onChange={onChangeHandler}
				onInput={setNowValue}
				tooltip={tooltip}
				value={nowValue}
			/>
			{after}
		</Wrapper>
	);
};
